import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BarChart3Icon, MapPinIcon, CalendarIcon, ZapIcon, ChevronDownIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
import { motion, AnimatePresence } from 'framer-motion';
const faqs = [{
  question: 'How did Viridia help TFP Landscaping get more leads?',
  answer: 'We implemented our Business Essentials package, which includes GEO-tuned paid ad campaigns and an AI-powered Request a Quote (RAQ) system. By responding instantly to inquiries instead of waiting 4+ hours, TFP was able to capture leads before competitors could.'
}, {
  question: 'What is the Business Essentials package?',
  answer: 'The Business Essentials package combines targeted local ad management with our AI lead conversion system. It ensures that the traffic generated by ads is immediately engaged and nurtured into booked appointments.'
}, {
  question: 'How does AI book estimates automatically?',
  answer: "Our AI is integrated directly into the lead platforms your customers use. When a customer requests a quote, the AI instantly replies, asks qualifying questions based on the business's services, and provides a booking link or schedules the estimate directly on the calendar."
}, {
  question: 'What results did TFP Landscaping see in 30 days?',
  answer: 'In just 30 days, TFP Landscaping saw a 284% increase in leads, totaling 242 inbound leads. They achieved a 39.95% close rate on those leads, with 28 estimates booked end-to-end entirely by the AI system.'
}, {
  question: 'How did Viridia help Finish Coat Painting increase revenue?',
  answer: 'Using the Never Miss a Lead package, Viridia implemented AI-powered messaging to handle inbound lead platform conversations instantly. In 30 days, the AI booked 29 estimates, generating $15.05k in closed-won revenue from end-to-end AI bookings alone - not including revenue from phone calls or website leads.'
}, {
  question: 'Can AI follow-up actually close jobs for contractors?',
  answer: 'Yes. The Finish Coat Painting case study proves that AI follow-up does more than just respond fast - it helps close real jobs. By engaging high-intent local demand immediately, AI turns inquiries into booked estimates and closed revenue.'
}];
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-800 last:border-0" data-id="element-1658" data-mp="2ieyzcb3xvlgo">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" data-id="element-1659" data-mp="2ifgxxee2qbrr">
        <span className="text-lg font-bold text-white group-hover:text-viridia transition-colors" data-id="element-1660" data-mp="28fzqosj6bpto">
          {question}
        </span>
        <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ml-4 ${isOpen ? 'rotate-180 text-viridia' : ''}`} data-id="element-1661" data-mp="23k17rkgm700n" />
      </button>
      <AnimatePresence data-id="element-1662" data-mp="1yb9m0ewdy7lr">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-1663" data-mp="23bnvm1dmxplw">
            <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-1664" data-mp="1kkeizj8kikq3">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function CaseStudiesPage() {
  return <div className="bg-viridia-black min-h-screen pt-24" data-id="element-1665" data-mp="rwipeneap8xj">
      <SEOHead title="Case Studies | Real Results for Local Businesses | Viridia Analytics" description="See how Viridia Analytics helps home service businesses like TFP Landscaping and Finish Coat Painting capture more leads, automate follow-up, and book more jobs." canonical="/case-studies" keywords="marketing case studies, HVAC marketing results, plumber marketing success, local business growth, painters, landscapers, contractors, window installers, marketing agency phoenix" faq={faqs} data-id="element-1666" data-mp="wvlirakwxqpo" />

      {/* HERO SECTION */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center" data-id="element-1667" data-mp="3hdn06a37esgq">
        <ScrollReveal data-id="element-1668" data-mp="3dc9rztcn2d6n">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-6 border border-viridia/20" data-id="element-1669" data-mp="3r9yaiuj8h88j">
            PROVEN RESULTS
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 tracking-tight" data-id="element-1670" data-mp="1wvfcp59mzswu">
            Case Studies: Real Leads, Real Estimates,{' '}
            <span className="text-viridia" data-id="element-1671" data-mp="1rz7121unehuf">Real Revenue</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto" data-id="element-1672" data-mp="7fldc77vvg0k">
            These are real client outcomes from AI follow-up, paid ad campaigns,
            and local lead conversion systems. We build systems that actually
            generate revenue for local service businesses.
          </p>
        </ScrollReveal>
      </section>

      {/* CASE STUDIES HUB */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-1673" data-mp="3ksohbw61mt2t">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-1674" data-mp="3psja659dpsqi">
          {/* TFP Landscaping Card */}
          <ScrollReveal delay={0.1} data-id="element-1675" data-mp="3aw1xnapge82d">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl overflow-hidden shadow-2xl h-full flex flex-col group hover:border-blue-500/50 transition-colors" data-id="element-1676" data-mp="3ax393gadswkm">
              <div className="bg-gray-900 p-8 relative overflow-hidden border-b border-gray-800" data-id="element-1677" data-mp="1958cm7wp4ymz">
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-[80px]" data-id="element-1678" data-mp="1e274ejsw0ti0"></div>
                <div className="relative z-10" data-id="element-1679" data-mp="1cmw1btmffera">
                  <h2 className="text-3xl font-bold text-white mb-6" data-id="element-1680" data-mp="17mzy3wljzrcs">
                    TFP Landscaping
                  </h2>
                  <div className="flex flex-wrap gap-2 mb-6" data-id="element-1681" data-mp="12n58rxhff9tu">
                    <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-viridia-black border border-gray-700 text-gray-300 text-xs" data-id="element-1682" data-mp="ymbiixgihixs">
                      Landscaping
                    </div>
                    <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-viridia-black border border-gray-700 text-gray-300 text-xs" data-id="element-1683" data-mp="1qdhqu16dymy5">
                      <MapPinIcon className="w-3 h-3 mr-1 text-blue-400" data-id="element-1684" data-mp="1llgcm9pnqm5h" />
                      Greater Phoenix
                    </div>
                    <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-viridia-black border border-gray-700 text-gray-300 text-xs" data-id="element-1685" data-mp="1hjcytefin5h1">
                      <ZapIcon className="w-3 h-3 mr-1 text-blue-400" data-id="element-1686" data-mp="29banwmubw2nm" />
                      Business Essentials
                    </div>
                    <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-viridia-black border border-gray-700 text-gray-300 text-xs" data-id="element-1687" data-mp="3s16gdollfp1c">
                      <CalendarIcon className="w-3 h-3 mr-1 text-blue-400" data-id="element-1688" data-mp="4u4exdyb1gdh" />
                      Client since July 2025
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-1689" data-mp="eslf91j4v7c7">
                    <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-1690" data-mp="esbk6zj8e9zn">
                      <p className="text-2xl font-bold text-blue-400 mb-1" data-id="element-1691" data-mp="jq3kagu152ex">
                        284%
                      </p>
                      <p className="text-gray-400 text-xs" data-id="element-1692" data-mp="sjytq126pf0d">Lead Increase</p>
                    </div>
                    <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-1693" data-mp="xj32iql75aq6">
                      <p className="text-2xl font-bold text-blue-400 mb-1" data-id="element-1694" data-mp="12i5vvdrtdsib">
                        242
                      </p>
                      <p className="text-gray-400 text-xs" data-id="element-1695" data-mp="de7mg8cm6hmv">Total Leads</p>
                    </div>
                    <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-1696" data-mp="8gpa07ze1ka4">
                      <p className="text-2xl font-bold text-blue-400 mb-1" data-id="element-1697" data-mp="mg70qsw8v7m4">
                        28
                      </p>
                      <p className="text-gray-400 text-xs" data-id="element-1698" data-mp="ifeqpxfjsyos">
                        AI-Booked Estimates
                      </p>
                    </div>
                    <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-1699" data-mp="3lv2fyx7iupoa">
                      <p className="text-2xl font-bold text-blue-400 mb-1" data-id="element-1700" data-mp="3gkt4gukinjd">
                        39.9%
                      </p>
                      <p className="text-gray-400 text-xs" data-id="element-1701" data-mp="3uufnvlfzz66w">Close Rate</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-8 flex-grow flex flex-col justify-between" data-id="element-1702" data-mp="334b0ddwxy31g">
                <p className="text-gray-400 leading-relaxed mb-8" data-id="element-1703" data-mp="33288spg541um">
                  One-man operation went from 4+ hour response times to instant
                  AI follow-up. Using the Business Essentials package, TFP
                  captured high-intent local demand and booked estimates
                  automatically.
                </p>
                <Link to="/case-studies/tfp-landscaping" className="inline-flex items-center text-blue-400 font-bold hover:text-blue-300 transition-colors" data-id="element-1704" data-mp="2o0hyu34btkwn">
                  Read Full Case Study{' '}
                  <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1705" data-mp="3gnm6ygypufcn" />
                </Link>
              </div>
            </div>
          </ScrollReveal>

          {/* Finish Coat Painting Card */}
          <ScrollReveal delay={0.2} data-id="element-1706" data-mp="36tvl7jtz4j0n">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl overflow-hidden shadow-2xl h-full flex flex-col group hover:border-orange-500/50 transition-colors" data-id="element-1707" data-mp="1qdsqlbj16c2c">
              <div className="bg-gray-900 p-8 relative overflow-hidden border-b border-gray-800" data-id="element-1708" data-mp="ocqqsbrno32e">
                <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full blur-[80px]" data-id="element-1709" data-mp="tbtxe0naahch"></div>
                <div className="relative z-10" data-id="element-1710" data-mp="en7bfg35irca">
                  <h2 className="text-3xl font-bold text-white mb-6" data-id="element-1711" data-mp="eme7snzhohyd">
                    Finish Coat Painting
                  </h2>
                  <div className="flex flex-wrap gap-2 mb-6" data-id="element-1712" data-mp="5m93wnti81oy">
                    <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-viridia-black border border-gray-700 text-gray-300 text-xs" data-id="element-1713" data-mp="5koaj1ixeoso">
                      Painting
                    </div>
                    <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-viridia-black border border-gray-700 text-gray-300 text-xs" data-id="element-1714" data-mp="3rva9onog1wkj">
                      <ZapIcon className="w-3 h-3 mr-1 text-orange-500" data-id="element-1715" data-mp="1q1ku86j1sglg" />
                      Never Miss a Lead
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-1716" data-mp="1uz31lshf2nrc">
                    <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-1717" data-mp="3fk9hx76xqygf">
                      <p className="text-2xl font-bold text-orange-500 mb-1" data-id="element-1718" data-mp="3al6dnoys4w9i">
                        29
                      </p>
                      <p className="text-gray-400 text-xs" data-id="element-1719" data-mp="3pacefhd9muji">
                        AI-Booked Estimates
                      </p>
                    </div>
                    <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-1720" data-mp="23jzulfq6cx4">
                      <p className="text-2xl font-bold text-orange-500 mb-1" data-id="element-1721" data-mp="3u8vzlfk1c2at">
                        $15.05k
                      </p>
                      <p className="text-gray-400 text-xs font-semibold" data-id="element-1722" data-mp="c1zjy4gc4lx4">
                        Closed-Won (AI Only)
                      </p>
                    </div>
                    <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-1723" data-mp="2duxfi81hxslr">
                      <p className="text-2xl font-bold text-orange-500 mb-1" data-id="element-1724" data-mp="28vkqphr2ebgj">
                        138
                      </p>
                      <p className="text-gray-400 text-xs" data-id="element-1725" data-mp="cthf0jvx15uk">Total Leads</p>
                    </div>
                    <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-1726" data-mp="hqzrgk95637b">
                      <p className="text-2xl font-bold text-orange-500 mb-1" data-id="element-1727" data-mp="lqru6gbutf4r">
                        64%
                      </p>
                      <p className="text-gray-400 text-xs" data-id="element-1728" data-mp="vqin4dkrnm2q">Lead Growth</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-8 flex-grow flex flex-col justify-between" data-id="element-1729" data-mp="10o0z6cu2yhey">
                <p className="text-gray-400 leading-relaxed mb-8" data-id="element-1730" data-mp="15hd8zgu3ojnn">
                  AI handled inbound lead platform conversations and booked
                  estimates while the team was on job sites. The Never Miss a
                  Lead package turned missed opportunities into $15.05k of
                  closed revenue from end-to-end AI bookings alone.
                </p>
                <Link to="/case-studies/finish-coat-painting" className="inline-flex items-center text-orange-500 font-bold hover:text-orange-400 transition-colors" data-id="element-1731" data-mp="1tkjo9whvgciz">
                  Read Full Case Study{' '}
                  <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1732" data-mp="1jnnm08ikgmmv" />
                </Link>
              </div>
            </div>
          </ScrollReveal>

          {/* More Coming Soon Card */}
          <ScrollReveal delay={0.3} data-id="element-1733" data-mp="2c9qavulsdoa2">
            <div className="bg-viridia-surface/50 border border-gray-800 border-dashed rounded-2xl p-8 h-full flex flex-col justify-center items-center text-center relative overflow-hidden min-h-[300px]" data-id="element-1734" data-mp="27borf8kg7my2">
              <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center mb-4" data-id="element-1735" data-mp="1yjmtbgxuqiqy">
                <BarChart3Icon className="w-8 h-8 text-gray-500" data-id="element-1736" data-mp="2v199zqcowi6t" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-1737" data-mp="2qaj1w5cvq18f">
                More Success Stories Coming Soon
              </h3>
              <p className="text-sm text-gray-400 max-w-xs" data-id="element-1738" data-mp="2arhvo963uwsh">
                We are currently compiling data for our latest client wins.
                Check back soon for more documented results.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-1739" data-mp="25toobct2fwqm">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16" data-id="element-1740" data-mp="2tjsgmo0t0a2b">
          <ScrollReveal data-id="element-1741" data-mp="2yidlj0r03hx3">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-1742" data-mp="2ygixveochbpb">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-400" data-id="element-1743" data-mp="2komjsejih2bx">
              Everything you need to know about working with Viridia Analytics.
            </p>
          </ScrollReveal>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1744" data-mp="3cgbwrxa0yy8d">
          <ScrollReveal delay={0.2} data-id="element-1745" data-mp="3h9dd89pvr5u2">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 md:p-8" data-id="element-1746" data-mp="3hag3cffdp48x">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-1747" data-mp="10ut7w8w1zidd" data-mpi={index} />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-br from-viridia-dark to-viridia relative overflow-hidden mt-12" data-id="element-1748" data-mp="1am5xsuyajygr">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-1749" data-mp="hvf99dndpjgj"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-1750" data-mp="wtpppfqj9a8z">
          <ScrollReveal data-id="element-1751" data-mp="rtbr0oatncnm">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-1752" data-mp="rs0h06qs98o2">
              Want to Be Our Next Case Study?
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto" data-id="element-1753" data-mp="238qpjwioicku">
              Book a free strategy call and let's build a system that captures
              leads, responds faster, and books more jobs for your business.
            </p>
            <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-white text-viridia-dark font-bold text-lg hover:bg-gray-100 transition-colors duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1" data-id="element-1754" data-mp="2f9qddc3bhisf">
              Book Your Free Strategy Call
              <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1755" data-mp="1rjmax81v7p30" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}